import { useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Smartphone, ScanLine, Loader2, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Charge on Reader dialog — launches Square POS on an Android device to accept a card payment
 * via the Square Reader. Shows a tap-target on phones and a QR code on desktop.
 *
 * NOTE: Square POS API only accepts real card payments in PRODUCTION. In sandbox mode the
 * intent URL builds fine but Square POS will refuse the launch.
 */
export function ChargeOnReaderDialog({ open, onOpenChange, ticket }) {
  const suggested = Number(ticket?.total_charged) > 0
    ? Number(ticket.total_charged).toFixed(2)
    : "";
  const [amount, setAmount] = useState(suggested);
  const [note, setNote] = useState(
    `Ticket #${(ticket?.id || "").slice(0, 8).toUpperCase()}${
      ticket?.appliance_type ? " · " + ticket.appliance_type : ""
    }`,
  );
  const [busy, setBusy] = useState(false);
  const [launch, setLaunch] = useState(null); // { intent_url, callback_url, environment }
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!open) {
      setLaunch(null);
      setBusy(false);
      setAmount(suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia("(max-width: 767px)").matches);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const cents = useMemo(() => Math.round(Number(amount) * 100), [amount]);

  const build = async () => {
    if (!(cents > 0)) {
      toast.error("Enter an amount first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/square/pos-launch-url`, {
        amount_cents: cents,
        description: note,
      });
      setLaunch(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="charge-reader-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-blue-700" />
            Charge on Square Reader
          </DialogTitle>
          <DialogDescription>
            {isMobile
              ? "Tap the button below on the phone that has Square POS installed. Customer taps their card on the reader."
              : "Enter the amount, then scan the QR code with the phone that has Square POS installed."}
          </DialogDescription>
        </DialogHeader>

        {!launch && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reader-amount">Amount ($)</Label>
              <Input
                id="reader-amount"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 180.00"
                autoFocus
                data-testid="reader-amount"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reader-note">Note (shows on Square receipt)</Label>
              <Textarea
                id="reader-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                className="min-h-[70px]"
                data-testid="reader-note"
              />
            </div>
          </div>
        )}

        {launch && (
          <div className="space-y-4">
            {launch.environment === "sandbox" && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid="sandbox-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <strong>Sandbox mode.</strong> Square POS will not launch a real reader in sandbox.
                  Switch to production Square credentials before using this at a job site.
                </div>
              </div>
            )}

            <div className="text-center space-y-1">
              <div className="text-3xl font-bold text-blue-900">
                ${(cents / 100).toFixed(2)}
              </div>
              <div className="text-xs text-slate-500 truncate">{launch.note}</div>
            </div>

            {isMobile ? (
              <a
                href={launch.intent_url}
                className="block w-full text-center bg-blue-700 hover:bg-blue-800 text-white font-semibold py-4 rounded-md"
                data-testid="reader-open-square"
              >
                <span className="inline-flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Open Square POS to charge
                </span>
              </a>
            ) : (
              <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-md border">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <ScanLine className="w-4 h-4" />
                  Scan with the tech's Android phone
                </div>
                <div className="bg-white p-3 rounded" data-testid="reader-qr">
                  <QRCodeCanvas value={launch.intent_url} size={220} includeMargin={false} level="M" />
                </div>
                <a
                  href={launch.intent_url}
                  className="text-xs text-blue-700 underline"
                  data-testid="reader-open-manual"
                >
                  or tap here if this device has Square POS
                </a>
              </div>
            )}

            <div className="text-xs text-slate-500 space-y-1">
              <p>1. Square POS opens with the amount pre-filled.</p>
              <p>2. Customer taps or inserts their card on the reader.</p>
              <p>3. Square POS bounces back here — the ticket is marked paid automatically.</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {!launch ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reader-cancel">
                Cancel
              </Button>
              <Button
                onClick={build}
                disabled={busy}
                className="bg-blue-700 hover:bg-blue-800"
                data-testid="reader-build"
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isMobile ? "Continue" : "Show QR code"}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setLaunch(null)} data-testid="reader-back">
              Change amount
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChargeOnReaderDialog;
